
import React, { useRef } from "react";
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal'; // 👈 make sure you have Modal
import api from '../../api/api';

const OrderItems = ({ orderId, customerId, orderDate, items, user, setError, refetchData }) => {
  const [availableItems, setAvailableItems] = React.useState([]);
  const [selectedItemId, setSelectedItemId] = React.useState('');
  const [rate, setRate] = React.useState('');
  const [quantity, setQuantity] = React.useState('');
  const [discountPer, setDiscountPer] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const itemNameRef = useRef(null);
  const [itemSearch, setItemSearch] = React.useState('');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [filteredItems, setFilteredItems] = React.useState([]);
  const [stockData, setStockData] = React.useState([]);
  const [cpid, setCpid] = React.useState(0);
  const [vrid, setVrid] = React.useState(0);
  const qtyRef = useRef(null);
  // 🔹 Modal + edit states
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editItemId, setEditItemId] = React.useState(null);

  const getDiscPer = (item) => {
    if (!item) return 0;
    const keys = ["DiscPer", "discper", "Disc%", "Discount", "discount", "DiscountPer", "discountper", "DISC_PER"];
    for (const key of keys) {
      if (item[key] !== undefined && item[key] !== null) {
        return Number(item[key]) || 0;
      }
    }
    return 0;
  };
  React.useEffect(() => {
    if (!itemSearch.trim()) {
      setFilteredItems(availableItems);
    } else {
      const search = itemSearch.toLowerCase();
      const filtered = availableItems.filter(item =>
        item.ItemNo?.toLowerCase().includes(search) ||
        item.barcode?.toLowerCase().includes(search)
      );
      setFilteredItems(filtered);
    }
  }, [itemSearch, availableItems]);
  React.useEffect(() => {
    if (isModalOpen && selectedItemId) {
      fetchStockData(selectedItemId);
    }
  }, [isModalOpen, selectedItemId]);

  React.useEffect(() => {
    api.getSelectedOrder(orderId)
      .then(data => {
        const normalized = (data.Table1 || []).map(i => ({
          ...i,
          Itemid: i.Itemid ?? i.ItemId ?? 0,
        }));
        setAvailableItems(normalized);

        if (data.Table && data.Table.length > 0) {
          setCpid(data.Table[0].cpid || 0);
          setVrid(data.Table[0].Vrid || 0);
        }
      })
      .catch(() => setError("Failed to load available items."));
  }, [orderId, setError]);


  React.useEffect(() => {
    const match = availableItems.find(
      i => i.ItemNo?.toLowerCase() === itemSearch.toLowerCase() ||
        i.barcode?.toLowerCase() === itemSearch.toLowerCase()
    );
    if (match) setSelectedItemId(match.ItemId);
  }, [itemSearch, availableItems]);


  const handleItemSelect = (itemId) => {
    const item = availableItems.find(i => i.Itemid === parseInt(itemId));
    if (item) {
      setSelectedItemId(item.Itemid);
      setRate(item.Rate);
      setDiscountPer(getDiscPer(item));
      setItemSearch(item.ItemNo);

      // 🔹 fetch stock for selected item
      fetchStockData(item.Itemid);
    }
    setIsDropdownOpen(false);

    // 🔹 move focus to Quantity after selection
    setTimeout(() => qtyRef.current?.focus(), 50);
  };
  const fetchStockData = async (itemId) => {
    try {
      const res = await api.StockQtyItemWise(itemId);
      setStockData(res?.Table || []);
    } catch (err) {
      console.error("Stock fetch error:", err);
      setStockData([]);
    }
  };

  const handleSaveItem = async (stayOpen = false) => {
    let itemId = selectedItemId;

    if (!editItemId && !itemId) {
      const match = availableItems.find(
        i =>
          i.ItemNo?.trim().toLowerCase() === itemSearch.trim().toLowerCase() ||
          i.barcode?.trim() === itemSearch.trim()
      );
      if (match) {
        itemId = match.Itemid ?? match.ItemId;
      } else {
        setError("Please select an item and enter quantity, rate, and discount.");
        return;
      }
    }

    const qty = parseFloat(quantity);
    const rt = parseFloat(rate);
    const discPer = parseFloat(discountPer || 0);

    if (qty <= 0 || rt <= 0) {
      setError("Quantity and Rate must be greater than 0.");
      return;
    }

    const amount = qty * rt * (1 - discPer / 100);

    const payload = {
      Itemid: itemId,
      SessionId: "9944010155",
      Vid: customerId,
      User: user?.UsrName || "admin",
      OrderDate: new Date(orderDate).toISOString().split("T")[0],
      Qty: qty,
      Rate: rt,
      Amount: amount,
      Notes: "-",
      ThisPoId: orderId,
      DiscPer: discPer,
      Seqid: editItemId || 0
    };

    setLoading(true);
    try {
      const resp = await api.MobSaveOrderDetailsRevAPI(payload);
      await refetchData();

      resetForm();

      if (!stayOpen) {
        setIsModalOpen(false); // close modal only in normal mode
      } else {
        setTimeout(() => itemNameRef.current?.focus(), 50); // refocus on Item Name
      }
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save item.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    setLoading(true);
    try {
      await api.deleteOrderItem(orderId, itemId);
      await refetchData();
    } catch (err) {
      console.error("Delete error:", err.response || err);
      setError("Failed to delete item.");
    } finally {
      setLoading(false);
    }
  };
  const handleEditItem = (item) => {
    const id = item.Itemid ?? item.ItemId ?? 0;

    setEditItemId(item.seqid ?? item.Seqid ?? 0);
    setSelectedItemId(id);
    setItemSearch(item.ItemNo || item.Partno || "");
    setQuantity(item.Qty ?? item.qty ?? "");
    setRate(item.Rate ?? item.rate ?? "");
    setDiscountPer(getDiscPer(item));
    setIsModalOpen(true);

    // 🔹 fetch stock when editing
    if (id) {
      fetchStockData(id);
    }
  };

  const resetForm = () => {
    setEditItemId(null);
    setSelectedItemId('');
    setQuantity('');
    setRate('');
    setDiscountPer('');
    setItemSearch('');
  };

  const totalAmount = items.reduce(
    (sum, item) => sum + ((item.Rate || 0) * (item.Qty || 0) * (1 - (getDiscPer(item) / 100))),
    0
  );

  return (
    <div className="w-full">
      <div className="mb-2 text-right">
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }} className="px-2 py-1 bg-blue-500 text-white">
          + Add
        </Button>
      </div>

      {/* Modal for Add/Edit */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
        title={editItemId ? "Edit Item" : "Add New Item"}>
        <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-5">
          {/* Item Search */}

          <div className="relative sm:col-span-2">
            <label className="block text-sm font-medium">Item Name*</label>
            <input
              ref={itemNameRef}   // 🔹 assign ref
              type="text"
              value={itemSearch}
              onChange={e => {
                setItemSearch(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => {
                setIsDropdownOpen(true);
                if (!itemSearch) setFilteredItems(availableItems);
              }}
              onKeyDown={(e) => {
                if (isDropdownOpen && filteredItems.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSelectedItemId(prev => {
                      const currentIndex = filteredItems.findIndex(i => i.Itemid === prev);
                      const nextIndex = (currentIndex + 1) % filteredItems.length;
                      return filteredItems[nextIndex].Itemid;
                    });
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSelectedItemId(prev => {
                      const currentIndex = filteredItems.findIndex(i => i.Itemid === prev);
                      const prevIndex =
                        (currentIndex - 1 + filteredItems.length) % filteredItems.length;
                      return filteredItems[prevIndex].Itemid;
                    });
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    if (selectedItemId) {
                      handleItemSelect(selectedItemId);
                    }
                    // 🔹 behave like Tab → focus Quantity
                    setTimeout(() => qtyRef.current?.focus(), 50);
                  }
                }
              }}
              className="block w-full px-2 py-1 mt-1 border rounded"
              placeholder="Search item by name/code"
            />
            {isDropdownOpen && filteredItems.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-60 overflow-y-auto">
                {filteredItems.map(item => (
                  <li
                    key={item.ItemId}
                    onClick={() => handleItemSelect(item.ItemId)}
                    className={`px-2 py-1 cursor-pointer ${selectedItemId === item.Itemid
                      ? "bg-blue-100"
                      : "hover:bg-gray-100"
                      }`}
                  >
                    <strong>{item.ItemNo}</strong> - Rs.{item.Rate} - {getDiscPer(item)}%
                  </li>
                ))}
              </ul>
            )}
          </div>



          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium">Quantity*</label>
            <input
              ref={qtyRef}
              type="number"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              className="w-full border rounded px-2 py-1"
            />
          </div>

          {/* Rate */}
          <div>
            <label className="block text-sm font-medium">Rate*</label>
            <input type="number" value={rate} onChange={e => setRate(e.target.value)} className="w-full border rounded px-2 py-1" />
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm font-medium">Disc %</label>
            <input
              type="number"
              value={discountPer}
              onChange={e => setDiscountPer(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Tab" || e.key === "Enter") {
                  e.preventDefault();
                  await handleSaveItem(true); // quick entry → stay open
                }
              }}
              className="w-full border rounded px-2 py-1"
            />
          </div>
        </div>

        {/* Save / Update Button */}
        <div className="mt-4 text-right">
          <Button
            onClick={() => handleSaveItem(false)} // normal → close modal
            disabled={loading || cpid > 0 || vrid > 0}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            {loading ? "Saving..." : editItemId ? "Update Item" : "Add Item"}
          </Button>
        </div>

        {stockData.length > 0 && (
          <div className="mt-3 sm:col-span-5">
            <h4 className="font-semibold text-sm mb-2 text-gray-700">
              Stock Availability
            </h4>
            <div className="overflow-hidden rounded-md border border-gray-200 shadow-sm">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-2 py-1 text-right font-medium">StkQty</th>
                    <th className="px-2 py-1 text-left font-medium">Godown</th>
                  </tr>
                </thead>
                <tbody>
                  {stockData.map((row, idx) => {
                    let qtyClass =
                      row.StkQty < 0
                        ? "bg-red-100 text-red-700"
                        : row.StkQty === 0
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700";

                    return (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td
                          className={`px-2 py-1 text-right rounded-sm ${qtyClass}`}
                        >
                          {row.StkQty}
                        </td>
                        <td className="px-2 py-1">{row.GdName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {/* Items Table */}
      <h3 className="mt-4 mb-2 text-lg font-semibold">Order Items</h3>
      <table className="w-full text-sm border">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left">Item</th>
            <th className="px-2 py-2 text-right">Qty</th>
            <th className="px-2 py-2 text-right">Rate</th>
            <th className="px-2 py-2 text-right">Disc %</th>
            <th className="px-2 py-2 text-right">Total</th>
            <th className="px-2 py-2 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const id = item.Seqid ?? item.seqid ?? item.Itemid ?? item.ItemId ?? idx;
            const itemName = item.Partno ?? item.ItemNo ?? "Unnamed";
            const qty = Number(item.Qty ?? 0);
            const rate = Number(item.Rate ?? 0);
            const disc = getDiscPer(item);
            const total = rate * qty * (1 - disc / 100);

            return (
              <tr key={id} className="border-t">
                <td className="px-2 py-1">{itemName}</td>
                <td className="px-2 py-1 text-right">{qty}</td>
                <td className="px-2 py-1 text-right">{rate}</td>
                <td className="px-2 py-1 text-right">{disc}</td>
                <td className="px-2 py-1 text-right">{total}</td>
                <td className="px-2 py-1 text-center space-x-2">
                  <Button onClick={() => handleEditItem(item)} className="px-2 py-1 text-xs bg-yellow-500 hover:bg-yellow-600">Edit</Button>
                  <Button onClick={() => handleDeleteItem(id)} className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600">Delete</Button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="font-bold bg-gray-100">
            <td colSpan="4" className="p-2 text-right">Total Amount</td>
            <td className="p-2 text-right">{totalAmount.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default OrderItems;









// import React, { useRef } from "react";
// import Button from '../../components/ui/Button';
// import Spinner from '../../components/ui/Spinner';
// import Modal from '../../components/ui/Modal'; // 👈 make sure you have Modal
// import api from '../../api/api';

// const OrderItems = ({ orderId, customerId, orderDate, items, user, setError, refetchData }) => {
//   const [availableItems, setAvailableItems] = React.useState([]);
//   const [selectedItemId, setSelectedItemId] = React.useState('');
//   const [rate, setRate] = React.useState('');
//   const [quantity, setQuantity] = React.useState('');
//   const [discountPer, setDiscountPer] = React.useState('');
//   const [loading, setLoading] = React.useState(false);
//   const itemNameRef = useRef(null);
//   const [itemSearch, setItemSearch] = React.useState('');
//   const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
//   const [filteredItems, setFilteredItems] = React.useState([]);
//   const [stockData, setStockData] = React.useState([]);
//   const [cpid, setCpid] = React.useState(0);
//   const [vrid, setVrid] = React.useState(0);
//   const qtyRef = useRef(null);
//   // 🔹 Modal + edit states
//   const [isModalOpen, setIsModalOpen] = React.useState(false);
//   const [editItemId, setEditItemId] = React.useState(null);

//   const getDiscPer = (item) => {
//     if (!item) return 0;
//     const keys = ["DiscPer", "discper", "Disc%", "Discount", "discount", "DiscountPer", "discountper", "DISC_PER"];
//     for (const key of keys) {
//       if (item[key] !== undefined && item[key] !== null) {
//         return Number(item[key]) || 0;
//       }
//     }
//     return 0;
//   };
//   React.useEffect(() => {
//     if (!itemSearch.trim()) {
//       setFilteredItems(availableItems);
//     } else {
//       const search = itemSearch.toLowerCase();
//       const filtered = availableItems.filter(item =>
//         item.ItemNo?.toLowerCase().includes(search) ||
//         item.barcode?.toLowerCase().includes(search)
//       );
//       setFilteredItems(filtered);
//     }
//   }, [itemSearch, availableItems]);
//   React.useEffect(() => {
//     if (isModalOpen && selectedItemId) {
//       fetchStockData(selectedItemId);
//     }
//   }, [isModalOpen, selectedItemId]);

//   React.useEffect(() => {
//     api.getSelectedOrder(orderId)
//       .then(data => {
//         const normalized = (data.Table1 || []).map(i => ({
//           ...i,
//           Itemid: i.Itemid ?? i.ItemId ?? 0,
//         }));
//         setAvailableItems(normalized);

//         if (data.Table && data.Table.length > 0) {
//           setCpid(data.Table[0].cpid || 0);
//           setVrid(data.Table[0].Vrid || 0);
//         }
//       })
//       .catch(() => setError("Failed to load available items."));
//   }, [orderId, setError]);


//   React.useEffect(() => {
//     const match = availableItems.find(
//       i => i.ItemNo?.toLowerCase() === itemSearch.toLowerCase() ||
//         i.barcode?.toLowerCase() === itemSearch.toLowerCase()
//     );
//     if (match) setSelectedItemId(match.ItemId);
//   }, [itemSearch, availableItems]);


//   const handleItemSelect = (itemId) => {
//     const item = availableItems.find(i => i.Itemid === parseInt(itemId));
//     if (item) {
//       setSelectedItemId(item.Itemid);
//       setRate(item.Rate);
//       setDiscountPer(getDiscPer(item));
//       setItemSearch(item.ItemNo);

//       // 🔹 fetch stock for selected item
//       fetchStockData(item.Itemid);
//     }
//     setIsDropdownOpen(false);

//     // 🔹 move focus to Quantity after selection
//     setTimeout(() => qtyRef.current?.focus(), 50);
//   };
//   const fetchStockData = async (itemId) => {
//     try {
//       const res = await api.StockQtyItemWise(itemId);
//       setStockData(res?.Table || []);
//     } catch (err) {
//       console.error("Stock fetch error:", err);
//       setStockData([]);
//     }
//   };

//   const handleSaveItem = async (stayOpen = false) => {
//     let itemId = selectedItemId;

//     if (!editItemId && !itemId) {
//       const match = availableItems.find(
//         i =>
//           i.ItemNo?.trim().toLowerCase() === itemSearch.trim().toLowerCase() ||
//           i.barcode?.trim() === itemSearch.trim()
//       );
//       if (match) {
//         itemId = match.Itemid ?? match.ItemId;
//       } else {
//         setError("Please select an item and enter quantity, rate, and discount.");
//         return;
//       }
//     }

//     const qty = parseFloat(quantity);
//     const rt = parseFloat(rate);
//     const discPer = parseFloat(discountPer || 0);

//     if (qty <= 0 || rt <= 0) {
//       setError("Quantity and Rate must be greater than 0.");
//       return;
//     }

//     const amount = qty * rt * (1 - discPer / 100);

//     const payload = {
//       Itemid: itemId,
//       SessionId: "9944010155",
//       Vid: customerId,
//       User: user?.UsrName || "admin",
//       OrderDate: new Date(orderDate).toISOString().split("T")[0],
//       Qty: qty,
//       Rate: rt,
//       Amount: amount,
//       Notes: "-",
//       ThisPoId: orderId,
//       DiscPer: discPer,
//       Seqid: editItemId || 0
//     };

//     setLoading(true);
//     try {
//       const resp = await api.MobSaveOrderDetailsRevAPI(payload);
//       await refetchData();

//       resetForm();

//       if (!stayOpen) {
//         setIsModalOpen(false); // close modal only in normal mode
//       } else {
//         setTimeout(() => itemNameRef.current?.focus(), 50); // refocus on Item Name
//       }
//     } catch (err) {
//       console.error("Save error:", err);
//       setError("Failed to save item.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDeleteItem = async (itemId) => {
//     setLoading(true);
//     try {
//       await api.deleteOrderItem(orderId, itemId);
//       await refetchData();
//     } catch (err) {
//       console.error("Delete error:", err.response || err);
//       setError("Failed to delete item.");
//     } finally {
//       setLoading(false);
//     }
//   };
//   const handleEditItem = (item) => {
//     const id = item.Itemid ?? item.ItemId ?? 0;

//     setEditItemId(item.seqid ?? item.Seqid ?? 0);
//     setSelectedItemId(id);
//     setItemSearch(item.ItemNo || item.Partno || "");
//     setQuantity(item.Qty ?? item.qty ?? "");
//     setRate(item.Rate ?? item.rate ?? "");
//     setDiscountPer(getDiscPer(item));
//     setIsModalOpen(true);

//     // 🔹 fetch stock when editing
//     if (id) {
//       fetchStockData(id);
//     }
//   };

//   const resetForm = () => {
//     setEditItemId(null);
//     setSelectedItemId('');
//     setQuantity('');
//     setRate('');
//     setDiscountPer('');
//     setItemSearch('');
//   };

//   const totalAmount = items.reduce(
//     (sum, item) => sum + ((item.Rate || 0) * (item.Qty || 0) * (1 - (getDiscPer(item) / 100))),
//     0
//   );

//   return (
//     <div className="w-full">
//       <div className="mb-2 text-right">
//         <Button onClick={() => { resetForm(); setIsModalOpen(true); }} className="px-2 py-1 bg-blue-500 text-white">
//           + Add
//         </Button>
//       </div>

//       {/* Modal for Add/Edit */}
//       <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
//         title={editItemId ? "Edit Item" : "Add New Item"}>
//         <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-5">
//           {/* Item Search */}

//           <div className="relative sm:col-span-2">
//             <label className="block text-sm font-medium">Item Name*</label>
//             <input
//               ref={itemNameRef}   // 🔹 assign ref
//               type="text"
//               value={itemSearch}
//               onChange={e => {
//                 setItemSearch(e.target.value);
//                 setIsDropdownOpen(true);
//               }}
//               onFocus={() => {
//                 setIsDropdownOpen(true);
//                 if (!itemSearch) setFilteredItems(availableItems);
//               }}
//               onKeyDown={(e) => {
//                 if (isDropdownOpen && filteredItems.length > 0) {
//                   if (e.key === "ArrowDown") {
//                     e.preventDefault();
//                     setSelectedItemId(prev => {
//                       const currentIndex = filteredItems.findIndex(i => i.Itemid === prev);
//                       const nextIndex = (currentIndex + 1) % filteredItems.length;
//                       return filteredItems[nextIndex].Itemid;
//                     });
//                   } else if (e.key === "ArrowUp") {
//                     e.preventDefault();
//                     setSelectedItemId(prev => {
//                       const currentIndex = filteredItems.findIndex(i => i.Itemid === prev);
//                       const prevIndex =
//                         (currentIndex - 1 + filteredItems.length) % filteredItems.length;
//                       return filteredItems[prevIndex].Itemid;
//                     });
//                   } else if (e.key === "Enter") {
//                     e.preventDefault();
//                     if (selectedItemId) {
//                       handleItemSelect(selectedItemId);
//                     }
//                     // 🔹 behave like Tab → focus Quantity
//                     setTimeout(() => qtyRef.current?.focus(), 50);
//                   }
//                 }
//               }}
//               className="block w-full px-2 py-1 mt-1 border rounded"
//               placeholder="Search item by name/code"
//             />
//             {isDropdownOpen && filteredItems.length > 0 && (
//               <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-60 overflow-y-auto">
//                 {filteredItems.map(item => (
//                   <li
//                     key={item.ItemId}
//                     onClick={() => handleItemSelect(item.ItemId)}
//                     className={`px-2 py-1 cursor-pointer ${selectedItemId === item.Itemid
//                       ? "bg-blue-100"
//                       : "hover:bg-gray-100"
//                       }`}
//                   >
//                     <strong>{item.ItemNo}</strong> - Rs.{item.Rate} - {getDiscPer(item)}%
//                   </li>
//                 ))}
//               </ul>
//             )}
//           </div>



//           {/* Quantity */}
//           <div>
//             <label className="block text-sm font-medium">Quantity*</label>
//             <input
//               ref={qtyRef}
//               type="number"
//               value={quantity}
//               onChange={e => setQuantity(e.target.value)}
//               className="w-full border rounded px-2 py-1"
//             />
//           </div>

//           {/* Rate */}
//           <div>
//             <label className="block text-sm font-medium">Rate*</label>
//             <input type="number" value={rate} onChange={e => setRate(e.target.value)} className="w-full border rounded px-2 py-1" />
//           </div>

//           {/* Discount */}
//           <div>
//             <label className="block text-sm font-medium">Disc %</label>
//             <input
//               type="number"
//               value={discountPer}
//               onChange={e => setDiscountPer(e.target.value)}
//               onKeyDown={async (e) => {
//                 if (e.key === "Tab" || e.key === "Enter") {
//                   e.preventDefault();
//                   await handleSaveItem(true); // quick entry → stay open
//                 }
//               }}
//               className="w-full border rounded px-2 py-1"
//             />
//           </div>
//         </div>

//         {/* Save / Update Button */}
//         <div className="mt-4 text-right">
//           <Button
//             onClick={() => handleSaveItem(false)} // normal → close modal
//             disabled={loading || cpid > 0 || vrid > 0}
//             className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
//           >
//             {loading ? "Saving..." : editItemId ? "Update Item" : "Add Item"}
//           </Button>
//         </div>

//         {stockData.length > 0 && (
//           <div className="mt-3 sm:col-span-5">
//             <h4 className="font-semibold text-sm mb-2 text-gray-700">
//               Stock Availability
//             </h4>
//             <div className="overflow-hidden rounded-md border border-gray-200 shadow-sm">
//               <table className="w-full text-xs">
//                 <thead className="bg-gray-50 text-gray-600">
//                   <tr>
//                     <th className="px-2 py-1 text-right font-medium">StkQty</th>
//                     <th className="px-2 py-1 text-left font-medium">Godown</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {stockData.map((row, idx) => {
//                     let qtyClass =
//                       row.StkQty < 0
//                         ? "bg-red-100 text-red-700"
//                         : row.StkQty === 0
//                           ? "bg-yellow-100 text-yellow-700"
//                           : "bg-green-100 text-green-700";

//                     return (
//                       <tr
//                         key={idx}
//                         className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
//                       >
//                         <td
//                           className={`px-2 py-1 text-right rounded-sm ${qtyClass}`}
//                         >
//                           {row.StkQty}
//                         </td>
//                         <td className="px-2 py-1">{row.GdName}</td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}
//       </Modal>

//       {/* Items Table */}
//       <h3 className="mt-4 mb-2 text-lg font-semibold">Order Items</h3>
//       <table className="w-full text-sm border">
//         <thead className="bg-gray-50">
//           <tr>
//             <th className="px-2 py-2 text-left">Item</th>
//             <th className="px-2 py-2 text-right">Qty</th>
//             <th className="px-2 py-2 text-right">Rate</th>
//             <th className="px-2 py-2 text-right">Disc %</th>
//             <th className="px-2 py-2 text-right">Total</th>
//             <th className="px-2 py-2 text-center">Action</th>
//           </tr>
//         </thead>
//         <tbody>
//           {items.map((item, idx) => {
//             const id = item.Seqid ?? item.seqid ?? item.Itemid ?? item.ItemId ?? idx;
//             const itemName = item.Partno ?? item.ItemNo ?? "Unnamed";
//             const qty = Number(item.Qty ?? 0);
//             const rate = Number(item.Rate ?? 0);
//             const disc = getDiscPer(item);
//             const total = rate * qty * (1 - disc / 100);

//             return (
//               <tr key={id} className="border-t">
//                 <td className="px-2 py-1">{itemName}</td>
//                 <td className="px-2 py-1 text-right">{qty}</td>
//                 <td className="px-2 py-1 text-right">{rate}</td>
//                 <td className="px-2 py-1 text-right">{disc}</td>
//                 <td className="px-2 py-1 text-right">{total}</td>
//                 <td className="px-2 py-1 text-center space-x-2">
//                   <Button onClick={() => handleEditItem(item)} className="px-2 py-1 text-xs bg-yellow-500 hover:bg-yellow-600">Edit</Button>
//                   <Button onClick={() => handleDeleteItem(id)} className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600">Delete</Button>
//                 </td>
//               </tr>
//             );
//           })}
//         </tbody>
//         <tfoot>
//           <tr className="font-bold bg-gray-100">
//             <td colSpan="4" className="p-2 text-right">Total Amount</td>
//             <td className="p-2 text-right">{totalAmount.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</td>
//             <td></td>
//           </tr>
//         </tfoot>
//       </table>
//     </div>
//   );
// };

// export default OrderItems;





